<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import reportsAPI from 'dashboard/api/reports';
import {
  downloadCsvFile,
  generateFileName,
} from 'dashboard/helper/downloadHelper';
import LineChart from 'shared/components/charts/LineChart.vue';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const { t } = useI18n();

const isLoading = ref(false);
const errorMessage = ref('');
const generatedAt = ref(null);
const isBreakdownOpen = ref(true);

const filters = ref({
  from: 0,
  to: 0,
  inboxId: null,
});

const summary = ref({
  current_period: {},
  peak_required_agents: 0,
  avg_capacity_per_agent_per_hour: 0,
});

const series = ref([]);

const formatCount = value => Number(value || 0).toLocaleString();
const formatNumber = value => Number(value || 0).toFixed(2);

const currentPeriodLabel = computed(() => {
  const period = summary.value.current_period || {};
  if (!period.from || !period.to) return '-';
  return `${format(fromUnixTime(period.from), 'dd MMM yy')} - ${format(
    fromUnixTime(period.to),
    'dd MMM yy'
  )}`;
});

const chartRows = computed(() =>
  (series.value || []).map(row => ({
    ...row,
    required_agents: Number(row.required_agents || 0),
    avg_conversations: Number(row.avg_conversations || 0),
  }))
);

const hasData = computed(() =>
  chartRows.value.some(
    row => row.required_agents > 0 || row.avg_conversations > 0
  )
);

const chartCollection = computed(() => ({
  labels: chartRows.value.map(row => row.hour_label),
  datasets: [
    {
      label: t('STAFFING_PREDICTION_REPORT.REQUIRED_AGENTS'),
      data: chartRows.value.map(row => row.required_agents),
      borderColor: '#1d75e5',
      backgroundColor: '#1d75e5',
      pointRadius: 3,
      pointHoverRadius: 4,
      pointBorderWidth: 0,
      tension: 0.28,
      borderWidth: 2,
      fill: false,
    },
  ],
}));

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  plugins: {
    legend: { display: false },
  },
  interaction: {
    mode: 'nearest',
    intersect: false,
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: '#6b7280' },
    },
    y: {
      beginAtZero: true,
      grid: { color: '#e5e7eb' },
      ticks: {
        color: '#6b7280',
        precision: 0,
        stepSize: 1,
      },
    },
  },
};

const toCsvValue = value => {
  const stringValue = String(value ?? '');
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const rowsToCsv = (headers, rows) => {
  const csvRows = [headers, ...rows];
  return csvRows
    .map(row => row.map(column => toCsvValue(column)).join(','))
    .join('\n');
};

const fetchReport = async () => {
  if (!filters.value.from || !filters.value.to) return;

  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await reportsAPI.getStaffingPrediction({
      from: filters.value.from,
      to: filters.value.to,
      inboxId: filters.value.inboxId,
    });

    summary.value = response.data?.summary || summary.value;
    series.value = response.data?.series || [];
    generatedAt.value = new Date();
  } catch {
    errorMessage.value = t('STAFFING_PREDICTION_REPORT.FETCH_ERROR');
    series.value = [];
  } finally {
    isLoading.value = false;
  }
};

const onFilterChange = ({ from, to, selectedInbox }) => {
  filters.value = {
    from,
    to,
    inboxId: selectedInbox?.id,
  };
  fetchReport();
};

const downloadBreakdownCsv = () => {
  const headers = [
    t('STAFFING_PREDICTION_REPORT.SERIES'),
    ...chartRows.value.map(row => row.hour_label),
  ];
  const rows = [
    [
      t('STAFFING_PREDICTION_REPORT.REQUIRED_AGENTS'),
      ...chartRows.value.map(row => row.required_agents),
    ],
    [
      t('STAFFING_PREDICTION_REPORT.AVG_CONVERSATIONS'),
      ...chartRows.value.map(row => formatNumber(row.avg_conversations)),
    ],
  ];

  const csvContent = rowsToCsv(headers, rows);
  const fileName = generateFileName({
    type: 'staffing-prediction-breakdown',
    to: filters.value.to,
  });
  downloadCsvFile(fileName, csvContent);
};

const toggleBreakdown = () => {
  isBreakdownOpen.value = !isBreakdownOpen.value;
};

const generatedAtLabel = computed(() =>
  generatedAt.value
    ? generatedAt.value.toLocaleString()
    : t('LAST_7_DAYS_REPORT.NOT_GENERATED_YET')
);
</script>

<template>
  <div class="sp-page">
    <div class="sp-shell">
      <div class="sp-header">
        <div class="sp-header-left">
          <h1 class="sp-title">{{ t('STAFFING_PREDICTION_REPORT.TITLE') }}</h1>
        </div>
      </div>

      <div class="sp-toolbar">
        <CompactDateInboxFilters
          :show-add-filter="false"
          @filter-change="onFilterChange"
        />
      </div>

      <div class="sp-content">
        <div v-if="errorMessage" class="sp-error">
          {{ errorMessage }}
        </div>

        <section class="sp-card">
          <div class="sp-card-header">
            <div class="sp-card-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 3v18h18M7 14l3-3 3 2 4-5"
                />
              </svg>
              <span>{{ t('STAFFING_PREDICTION_REPORT.TITLE') }}</span>
            </div>
          </div>

          <div class="sp-metric-row">
            <div class="sp-metric-item">
              <div class="sp-metric-label">
                {{ t('STAFFING_PREDICTION_REPORT.PEAK_LABEL') }}
              </div>
              <div class="sp-metric-value">
                {{ formatCount(summary.peak_required_agents) }}
              </div>
            </div>
            <div class="sp-metric-item">
              <div class="sp-metric-label">
                {{ t('STAFFING_PREDICTION_REPORT.CAPACITY_LABEL') }}
              </div>
              <div class="sp-metric-value">
                {{ formatNumber(summary.avg_capacity_per_agent_per_hour) }}
              </div>
            </div>
            <div class="sp-metric-item sp-metric-item--period">
              <div class="sp-metric-label">Period</div>
              <div class="sp-metric-value sp-metric-value--period">
                {{ currentPeriodLabel }}
              </div>
            </div>
          </div>

          <div class="sp-chart">
            <woot-loading-state
              v-if="isLoading"
              :message="$t('REPORT.LOADING_CHART')"
              class="h-full"
            />
            <div v-else-if="!hasData" class="sp-empty">
              {{ t('STAFFING_PREDICTION_REPORT.NO_DATA') }}
            </div>
            <LineChart
              v-else
              :collection="chartCollection"
              :chart-options="chartOptions"
            />
          </div>

          <div class="sp-breakdown">
            <div
              class="sp-breakdown-header"
              role="button"
              tabindex="0"
              @click="toggleBreakdown"
              @keydown.enter.prevent="toggleBreakdown"
              @keydown.space.prevent="toggleBreakdown"
            >
              <div class="sp-breakdown-title">
                <svg
                  class="sp-breakdown-caret"
                  :class="{ 'is-collapsed': !isBreakdownOpen }"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="m6 9 6 6 6-6"
                  />
                </svg>
                <span>{{
                  t('STAFFING_PREDICTION_REPORT.BREAKDOWN_TITLE')
                }}</span>
              </div>
              <button
                class="sp-export-btn"
                type="button"
                @click.stop="downloadBreakdownCsv"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
                  />
                </svg>
                <span>{{ t('STAFFING_PREDICTION_REPORT.EXPORT_CSV') }}</span>
              </button>
            </div>

            <div v-show="isBreakdownOpen" class="sp-table-wrap">
              <table class="sp-table">
                <thead>
                  <tr>
                    <th class="sp-th-series">
                      {{ t('STAFFING_PREDICTION_REPORT.SERIES') }}
                    </th>
                    <th
                      v-for="(row, index) in chartRows"
                      :key="`staffing-header-${row.hour}`"
                      class="sp-th-data"
                      :class="
                        index % 2 === 0
                          ? 'sp-head-tone-odd'
                          : 'sp-head-tone-even'
                      "
                    >
                      {{ row.hour_label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="sp-row sp-row--required">
                    <td class="sp-td-series sp-series-tone-odd">
                      <span class="sp-series-label">
                        <span class="sp-dot sp-dot--blue" aria-hidden="true" />
                        <span>{{
                          t('STAFFING_PREDICTION_REPORT.REQUIRED_AGENTS')
                        }}</span>
                      </span>
                    </td>
                    <td
                      v-for="(row, index) in chartRows"
                      :key="`staffing-required-${row.hour}`"
                      class="sp-td-data"
                      :class="
                        index % 2 === 0
                          ? 'sp-body-tone-odd'
                          : 'sp-body-tone-even'
                      "
                    >
                      {{ formatCount(row.required_agents) }}
                    </td>
                  </tr>
                  <tr class="sp-row sp-row--avg">
                    <td class="sp-td-series sp-series-tone-even">
                      <span class="sp-series-label">
                        <span class="sp-dot sp-dot--amber" aria-hidden="true" />
                        <span>{{
                          t('STAFFING_PREDICTION_REPORT.AVG_CONVERSATIONS')
                        }}</span>
                      </span>
                    </td>
                    <td
                      v-for="(row, index) in chartRows"
                      :key="`staffing-avg-${row.hour}`"
                      class="sp-td-data"
                      :class="
                        index % 2 === 0
                          ? 'sp-body-tone-odd'
                          : 'sp-body-tone-even'
                      "
                    >
                      {{ formatNumber(row.avg_conversations) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <p class="sp-footer-note">
          {{
            t('STAFFING_PREDICTION_REPORT.GENERATED_AT', {
              time: generatedAtLabel,
            })
          }}<br />
          {{ t('LAST_7_DAYS_REPORT.LOCAL_TIME_NOTE') }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sp-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}

.sp-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.sp-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}

.sp-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}

.sp-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.sp-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
}

.sp-content {
  padding: 16px 32px;
}

.sp-error {
  margin-bottom: 12px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}

.sp-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  box-sizing: border-box;
  margin-bottom: 20px;
  overflow: hidden;
}

.sp-card-header {
  min-height: 68px;
  padding: 0 16px;
  border-bottom: 1px solid #dfe3e8;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sp-card-title {
  color: #111827;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.sp-metric-row {
  display: flex;
  flex-wrap: wrap;
  padding: 0 16px;
}

.sp-metric-item {
  padding: 12px 16px 16px 0;
  min-width: 240px;
}

.sp-metric-item--period {
  margin-left: auto;
  text-align: right;
}

.sp-metric-label {
  font-size: 12px;
  line-height: 20px;
  color: #4b5563;
}

.sp-metric-value {
  margin-top: 2px;
  color: #111827;
  font-size: 18px;
  line-height: 28px;
  font-weight: 600;
}

.sp-metric-value--period {
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: #111827;
}

.sp-chart {
  position: relative;
  height: 280px;
  margin: 0 16px 8px;
}

.sp-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}

.sp-breakdown {
  border-top: 1px solid #dfe3e8;
}

.sp-breakdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 16px;
  cursor: pointer;
  user-select: none;
}

.sp-breakdown-title {
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.sp-breakdown-caret {
  transition: transform 0.18s ease;
}

.sp-breakdown-caret.is-collapsed {
  transform: rotate(-90deg);
}

.sp-export-btn {
  min-width: 128px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
}

.sp-table-wrap {
  overflow-x: auto;
  border-top: 1px solid #dfe3e8;
}

.sp-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
  color: #424d57;
}

.sp-th-series,
.sp-td-series {
  position: sticky;
  left: 0;
  min-width: 180px;
  max-width: 180px;
  text-align: left;
  border-right: 1px solid #d7dde4;
  z-index: 2;
  overflow: visible;
}

.sp-th-series::after,
.sp-td-series::after {
  content: '';
  position: absolute;
  top: 0;
  right: -5px;
  width: 5px;
  height: 100%;
  background: linear-gradient(
    to left,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.05) 100%
  );
  pointer-events: none;
}

.sp-th-series {
  background: #f8fafc;
  color: #424d57 !important;
  padding: 10px 12px;
  font-weight: 600;
  z-index: 3;
}

.sp-th-data {
  background: #f8fafc;
  color: #424d57 !important;
  padding: 10px 12px;
  text-align: center;
  border-bottom: 1px solid #dfe3e8;
  white-space: nowrap;
  font-weight: 600;
}

.sp-td-series {
  background: #ffffff;
  padding: 12px;
  color: #111827;
  font-weight: 600;
}

.sp-td-data {
  background: #ffffff;
  padding: 12px;
  text-align: center;
  border-bottom: 1px solid #edf1f5;
  white-space: nowrap;
}

.sp-series-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.sp-dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  display: inline-block;
  flex: 0 0 auto;
}

.sp-dot--blue {
  background: #1d75e5;
}

.sp-dot--amber {
  background: #eecc1b;
}

.sp-head-tone-odd {
  background: #eaeef1;
}

.sp-head-tone-even {
  background: #eff3f5;
}

.sp-body-tone-odd {
  background: #f6f7f8;
}

.sp-body-tone-even {
  background: #fefefe;
}

.sp-series-tone-odd {
  background: #eaeef1;
}

.sp-series-tone-even {
  background: #eff3f5;
}

.sp-footer-note {
  margin: 12px 0 0;
  color: #6b7280;
  font-size: 12px;
  line-height: 20px;
}

:global(body.dark) .sp-page,
:global(body.dark) .sp-shell,
:global(body.dark) .sp-card,
:global(body.dark) .sp-header,
:global(body.dark) .sp-toolbar,
:global(body.dark) .sp-th-series,
:global(body.dark) .sp-th-data,
:global(body.dark) .sp-td-series,
:global(body.dark) .sp-td-data,
:global(body.theme-mix) .sp-page,
:global(body.theme-mix) .sp-shell,
:global(body.theme-mix) .sp-card,
:global(body.theme-mix) .sp-header,
:global(body.theme-mix) .sp-toolbar,
:global(body.theme-mix) .sp-th-series,
:global(body.theme-mix) .sp-th-data,
:global(body.theme-mix) .sp-td-series,
:global(body.theme-mix) .sp-td-data {
  background: #ffffff !important;
  color: #111827 !important;
}

:global(body.dark) .sp-table,
:global(body.dark) .sp-footer-note,
:global(body.theme-mix) .sp-table,
:global(body.theme-mix) .sp-footer-note {
  color: #424d57 !important;
}

:global(body.dark) .sp-head-tone-odd {
  background: #eaeef1 !important;
}

:global(body.dark) .sp-head-tone-even {
  background: #eff3f5 !important;
}

:global(body.dark) .sp-body-tone-odd {
  background: #f6f7f8 !important;
}

:global(body.dark) .sp-body-tone-even {
  background: #fefefe !important;
}

:global(body.dark) .sp-series-tone-odd {
  background: #eaeef1 !important;
}

:global(body.dark) .sp-series-tone-even {
  background: #eff3f5 !important;
}

:global(body.dark) .sp-table th,
:global(body.dark) .sp-table td,
:global(body.theme-mix) .sp-table th,
:global(body.theme-mix) .sp-table td {
  color: #111827 !important;
  border-color: #e5e7eb !important;
}

@media (max-width: 1100px) {
  .sp-content {
    padding: 12px 16px 16px;
  }

  .sp-toolbar {
    padding: 10px 16px 8px;
  }

  .sp-metric-item {
    min-width: 200px;
  }

  .sp-metric-item--period {
    margin-left: 0;
    text-align: left;
  }
}
</style>
